import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { clearTokens, getAccessToken, getMfaToken } from '@/api/auth';
import { meQuery, qk } from '@/api/queries';
import { paths } from '@/app/paths';
import { Button, Card, Input } from '@/design-system';
import { errorText } from '@/shell/errorText';
import { Logo } from '@/shell/Logo';
import { setupMfa, verifyMfa } from './api';
import { returnTo } from './LoginPage';
import { QrCode } from './QrCode';
import s from './auth.module.css';

/** «JBSW Y3DP EHPK 3PXP»: the secret in groups of four, easier to type by hand. */
export function groupSecret(secret: string): string {
  return secret.replace(/(.{4})(?=.)/g, '$1 ');
}

/**
 * /login/mfa. Two cases (P1-C01):
 * - an enrolled Admin after the password: verify the code with the login's `mfa_token`;
 * - an Admin not enrolled yet (a limited token that only reaches /me and MFA): set up first — the secret
 *   and its otpauth:// link — then verify, which enables MFA and returns a full session.
 */
export function MfaPage() {
  const { t } = useTranslation('auth');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const pending = getMfaToken();
  // Without the login's mfa_token this is enrolment: it needs the limited session (refreshed after a reload).
  const me = useQuery({ ...meQuery, enabled: !pending });
  const enrol = !pending && me.data?.role === 'admin' && !me.data.mfa_enabled;
  const [setup, setSetup] = useState<{ secret: string; otpauth_uri: string } | null>(null);

  // Exactly one setup per visit: a second call would replace the secret on the server (StrictMode runs
  // effects twice in development).
  const asked = useRef(false);
  useEffect(() => {
    if (!enrol || asked.current) return;
    asked.current = true;
    setupMfa()
      .then(setSetup)
      .catch((err: unknown) => setProblem(errorText(err, tc)));
  }, [enrol, tc]);

  if (!pending) {
    if (me.isPending) return null;
    // Nothing to verify here: a full session goes on, anyone else signs in first.
    const signedIn = !!me.data && (me.data.role !== 'admin' || me.data.mfa_verified);
    if (!enrol) return <Navigate to={signedIn ? paths.dashboard() : paths.login()} replace />;
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setProblem(null);
    try {
      const res = await verifyMfa(code);
      qc.removeQueries();
      if (res.user) qc.setQueryData(qk.me, res.user);
      navigate(returnTo(location.state), { replace: true });
    } catch (err) {
      setProblem(errorText(err, tc));
      setCode('');
    } finally {
      setBusy(false);
    }
  };

  const back = () => {
    if (getAccessToken() || pending) clearTokens();
    qc.removeQueries();
    navigate(paths.login());
  };

  return (
    <div className={s.page}>
      <div className={s.stack}>
        <Logo />
        <Card padding={24} texture="dotsGlow" className={s.card}>
          <div className={s.titles}>
            <h1 className={s.title}>{enrol ? t('mfa.setup_title') : t('mfa.title')}</h1>
            <span className={s.sub}>{enrol ? t('mfa.setup_sub') : t('mfa.sub')}</span>
          </div>
          {enrol && setup && (
            <div className={s.secret}>
              <div className={s.qr}>
                <QrCode value={setup.otpauth_uri} label={t('mfa.qr')} className={s.qrCode} />
                <span className={s.secretLabel}>{t('mfa.qr')}</span>
              </div>
              <span className={s.secretLabel}>{t('mfa.secret')}</span>
              <span className={s.secretValue} data-testid="mfa-secret">
                {groupSecret(setup.secret)}
              </span>
              <a className={s.secretLink} href={setup.otpauth_uri}>
                {t('mfa.open_app')}
              </a>
            </div>
          )}
          <form className={s.form} onSubmit={(e) => void submit(e)}>
            <Input
              label={t('mfa.code')}
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9 ]{6,7}"
              maxLength={7}
              required
              mono
              className={s.code}
              placeholder={t('mfa.code_placeholder')}
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
            {problem && (
              <span className={s.error} role="alert">
                {problem}
              </span>
            )}
            <Button type="submit" size={46} block spread trailingIcon="arrow-right" disabled={busy || (enrol && !setup)}>
              {enrol ? t('mfa.setup_submit') : t('mfa.submit')}
            </Button>
            <button type="button" className={s.back} onClick={back}>
              {t('mfa.back')}
            </button>
          </form>
        </Card>
        <span className={s.note}>{t('mfa.note')}</span>
      </div>
    </div>
  );
}
